/**
 * Aplicar uma regra do catálogo a vários WAFs (spec 2026-10-18 D9, consoleai#25239).
 *
 * A regra entra em Count: nada é bloqueado até a promoção, que é decidida na
 * página do lote depois da observação. A prévia diz por que cada WAF fica fora —
 * código do stage, já tem a regra, sem permissão — em vez de escondê-lo.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Badge, Button, Checkbox, Group, Loader, Modal, Select, Stack, Table, Text,
  Textarea } from '@mantine/core'
import { useMutation, useQuery } from '@tanstack/react-query'
import { formatApiError } from '@/api/errors'
import { criarLote, listarCatalogo, previaDoLote, type ItemDaPrevia,
  type SituacaoDoLote } from '@/api/waf'

const ROTULO: Record<SituacaoDoLote, { texto: string; cor: string }> = {
  aplicavel: { texto: 'Aplicável', cor: 'teal' },
  ja_tem: { texto: 'Já tem', cor: 'gray' },
  codigo: { texto: 'Código do stage', cor: 'violet' },
  sem_permissao: { texto: 'Sem permissão', cor: 'gray' },
  fora_de_escopo: { texto: 'Fora de escopo', cor: 'gray' },
}

export function PreviaDoLote({ itens, selecionados, onAlternar }: {
  itens: ItemDaPrevia[]
  selecionados: Set<number>
  onAlternar: (wafId: number) => void
}) {
  // Sem permissão não mostra nome nem conta: só a contagem.
  const visiveis = itens.filter((i) => i.situacao !== 'sem_permissao')
  const ocultos = itens.length - visiveis.length
  return (
    <Stack gap="xs">
      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th />
            <Table.Th>WAF</Table.Th>
            <Table.Th>Conta</Table.Th>
            <Table.Th>Situação</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {visiveis.map((i) => (
            <Table.Tr key={i.waf_id}>
              <Table.Td>
                <Checkbox
                  aria-label={`Incluir ${i.nome}`}
                  disabled={i.situacao !== 'aplicavel'}
                  checked={selecionados.has(i.waf_id)}
                  onChange={() => onAlternar(i.waf_id)}
                />
              </Table.Td>
              <Table.Td><Text size="sm" ff="monospace">{i.nome}</Text></Table.Td>
              <Table.Td><Text size="sm" ff="monospace">{i.account_id}</Text></Table.Td>
              <Table.Td>
                <Group gap={6}>
                  <Badge color={ROTULO[i.situacao].cor} variant="light">{ROTULO[i.situacao].texto}</Badge>
                  {i.motivo && <Text size="xs" c="dimmed">{i.motivo}</Text>}
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {ocultos > 0 && (
        <Text size="xs" c="dimmed">
          {ocultos} WAF(s) de tenants em que você não tem a permissão ficam fora.
        </Text>
      )}
    </Stack>
  )
}

export function WafLoteModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const [chave, setChave] = useState<string | null>(null)
  const [selecionados, setSelecionados] = useState<Set<number>>(new Set())
  const [motivo, setMotivo] = useState('')

  const catalogo = useQuery({ queryKey: ['waf-catalogo'], queryFn: listarCatalogo, enabled: opened })
  useEffect(() => {
    if (!chave && catalogo.data?.length) setChave(catalogo.data[0].chave)
  }, [catalogo.data, chave])
  const regra = useMemo(() => catalogo.data?.find((r) => r.chave === chave), [catalogo.data, chave])

  const previa = useQuery({
    queryKey: ['waf-lote-previa', chave],
    queryFn: () => previaDoLote(chave as string),
    enabled: opened && !!chave,
  })
  useEffect(() => {
    setSelecionados(new Set((previa.data?.itens ?? [])
      .filter((i) => i.situacao === 'aplicavel').map((i) => i.waf_id)))
  }, [previa.data])

  const enviar = useMutation({
    mutationFn: () => criarLote(chave as string, [...selecionados], motivo.trim()),
    onSuccess: (r) => { onClose(); navigate(`/security/waf/lotes/${r.lote_id}`) },
  })

  const alternar = (id: number) => setSelecionados((s) => {
    const n = new Set(s)
    if (n.has(id)) n.delete(id)
    else n.add(id)
    return n
  })

  return (
    <Modal opened={opened} onClose={onClose} size="xl" title="Aplicar regra do catálogo">
      <Stack gap="md">
        <Select
          label="Regra"
          data={(catalogo.data ?? []).map((r) => ({ value: r.chave, label: r.nome }))}
          value={chave}
          onChange={setChave}
          allowDeselect={false}
        />
        {regra && (
          <Stack gap={4}>
            <Text size="sm">{regra.descricao}</Text>
            <Text size="xs" c="dimmed">
              Casa: <Text span ff="monospace" size="xs">{regra.casa.join('  ')}</Text>
            </Text>
            <Text size="xs" c="dimmed">
              Não casa: <Text span ff="monospace" size="xs">{regra.nao_casa.join('  ')}</Text>
            </Text>
          </Stack>
        )}
        <Alert color="blue" variant="light">
          A regra entra em Count (observação) e não bloqueia nada. Na página do lote você
          vê quantas requisições casaram em cada WAF e escolhe quais promover a Block.
          Ambiente que exige GMUD espera a aprovação, uma GMUD por tenant.
        </Alert>
        {previa.isLoading && <Loader size="sm" />}
        {previa.error && <Alert color="red">{formatApiError(previa.error)}</Alert>}
        {previa.data && (
          <PreviaDoLote itens={previa.data.itens} selecionados={selecionados} onAlternar={alternar} />
        )}
        <Textarea
          label="Motivo"
          description="Vai para a trilha de auditoria e para a GMUD, quando houver."
          value={motivo}
          onChange={(e) => setMotivo(e.currentTarget.value)}
          minRows={2}
          required
        />
        {enviar.error && <Alert color="red">{formatApiError(enviar.error)}</Alert>}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button
            onClick={() => enviar.mutate()}
            loading={enviar.isPending}
            disabled={selecionados.size === 0 || motivo.trim().length < 5}
          >
            Aplicar em {selecionados.size} WAF(s)
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
